import React from 'react';
import { Container, Tabs, Tab } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import Select from 'react-select';

import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import InformationModal from '../../Components/InformationModal/InformationModal';
import { useSecurity } from '../../Contexts/SecurityContext';
import { Permissions } from '../../Security/permissions';
import OficialNoMatchWorkbenchTab from './OficialNoMatchWorkbenchTab';
import EntFinNoMatchWorkbenchTab from './EntFinNoMatchWorkbenchTab';
import ConciliaRelWorkbenchTab from './ConciliaRelWorkbenchTab';
import WorkbenchSuggestionsPanel from './WorkbenchSuggestionsPanel';
import { getStoredConciliationCompanyId, setStoredConciliationCompanyId } from './companySelectionStore';

import './ManualPostingPage.scss';

const useToken = () => {
  const { instance, accounts } = useMsal();

  return React.useCallback(async () => {
    const req = { ...loginRequest, account: accounts[0] };
    const { accessToken } = await instance.acquireTokenSilent(req);
    return accessToken;
  }, [instance, accounts]);
};

const ManualPostingPageContent = () => {
  const getToken = useToken();
  const { accounts } = useMsal();
  const { companiesFor, hasCompanyPermission, canExecuteForCompany } = useSecurity();

  const [companyId, setCompanyId] = React.useState(() => getStoredConciliationCompanyId());
  const [companyOptions, setCompanyOptions] = React.useState([]);
  const [loadingCompanies, setLoadingCompanies] = React.useState(false);
  const [gastosSuggestionsFilter, setGastosSuggestionsFilter] = React.useState(null);
  const [relSuggestionsFilter, setRelSuggestionsFilter] = React.useState(null);
  const [suggestionsRefreshTick, setSuggestionsRefreshTick] = React.useState(0);

  const [activeTab, setActiveTab] = React.useState('rel');

  const [infoModal, setInfoModal] = React.useState({ show: false, title: '', content: null });

  const showInfoModal = React.useCallback((title, content) => {
    setInfoModal({ show: true, title, content });
  }, []);

  const hideInfoModal = React.useCallback(() => {
    setInfoModal((prev) => ({ ...prev, show: false }));
  }, []);

  React.useEffect(() => {
    const opts = companiesFor(Permissions.Conciliation.ManualPosting).map(company => ({
      value: company.companyId,
      label: company.companyName || company.companyDb || `Company ${company.companyId}`
    }));
    setCompanyOptions(opts);
    setLoadingCompanies(false);
    if (companyId && !opts.some(option => option.value === companyId)) {
      setCompanyId(null);
      setStoredConciliationCompanyId(null);
    }
  }, [companiesFor, companyId]);

  const selectedCompany = React.useMemo(
    () => companyOptions.find((o) => o.value === companyId) ?? null,
    [companyOptions, companyId]
  );
  const currentUser = accounts?.[0]?.username ?? accounts?.[0]?.name ?? '';
  const companyCanView = !!companyId && hasCompanyPermission(companyId, Permissions.Conciliation.ManualPosting);
  const companyCanExecute = !!companyId && canExecuteForCompany(companyId, Permissions.Conciliation.ManualPosting);
  const authorizedCompanyId = companyCanView ? companyId : null;

  const defaultGastosFilter = React.useMemo(() => authorizedCompanyId ? ({
    companyId: authorizedCompanyId,
    status: 'OFICIALNOMATCH',
    page: 1,
    pageSize: 20,
    search: '',
    sortBy: 'GOFecha',
    sortDir: 'desc'
  }) : null, [authorizedCompanyId]);

  const defaultRelFilter = React.useMemo(() => authorizedCompanyId ? ({
    companyId: authorizedCompanyId,
    page: 1,
    pageSize: 20,
    search: '',
    sortBy: 'PFFecha',
    sortDir: 'desc'
  }) : null, [authorizedCompanyId]);

  React.useEffect(() => {
    setGastosSuggestionsFilter(null);
    setRelSuggestionsFilter(null);
  }, [companyId]);

  const currentGastosFilter = gastosSuggestionsFilter ?? defaultGastosFilter;
  const currentRelFilter = relSuggestionsFilter ?? defaultRelFilter;

  const refreshBothSuggestionTables = React.useCallback(() => {
    setSuggestionsRefreshTick((t) => t + 1);
  }, []);

  return (
    <Container fluid className="manual-posting-page h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center w-100 mb-2">
        <div style={{ minWidth: 280 }}>
          <Select
            classNamePrefix="react-select"
            options={[{ value: null, label: 'Select company...' }, ...companyOptions]}
            value={selectedCompany}
            onChange={(opt) => {
              const id = opt?.value ?? null;
              setCompanyId(id);
              setStoredConciliationCompanyId(id);
            }}
            isLoading={loadingCompanies}
            placeholder="Select company..."
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>

        <WorkbenchSuggestionsPanel
          companyCanExecute={companyCanExecute}
          conciliationType="both"
          gastosFilter={currentGastosFilter}
          relFilter={currentRelFilter}
          currentUser={currentUser}
          getToken={getToken}
          onFinished={refreshBothSuggestionTables}
        />
      </div>
      {!authorizedCompanyId && (
        <div className="alert alert-info mb-2">
          Select a company to load manual posting records.
        </div>
      )}

      <div className="manual-posting-tabs-wrap flex-grow-1 d-flex flex-column min-h-0">
        <Tabs
          id="manual-posting-workbench-tabs"
          aria-label="Manual posting workbench views"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'oficial')}
          className="manual-posting-tabs mb-0"
          mountOnEnter
          unmountOnExit
        >
          <Tab eventKey="rel" title="Invoice + Payment" className="h-100">
            <ConciliaRelWorkbenchTab
              companyCanExecute={companyCanExecute}
              companyId={authorizedCompanyId}
              getToken={getToken}
              showInfoModal={showInfoModal}
              externalRefreshTick={suggestionsRefreshTick}
              onSuggestionsFilterChange={setRelSuggestionsFilter}
            />
          </Tab>

          <Tab eventKey="oficial" title="Purchase Invoice" className="h-100">
            <OficialNoMatchWorkbenchTab
              companyCanExecute={companyCanExecute}
              companyId={authorizedCompanyId}
              getToken={getToken}
              showInfoModal={showInfoModal}
              externalRefreshTick={suggestionsRefreshTick}
              onSuggestionsFilterChange={setGastosSuggestionsFilter}
            />
          </Tab>

          <Tab eventKey="entfin" title="Vendor Payment" className="h-100">
            <EntFinNoMatchWorkbenchTab
              companyCanExecute={companyCanExecute}
              companyId={authorizedCompanyId}
              getToken={getToken}
              showInfoModal={showInfoModal}
            />
          </Tab>
        </Tabs>
      </div>

      <InformationModal
        show={infoModal.show}
        onHide={hideInfoModal}
        onAcceptClick={hideInfoModal}
        title={infoModal.title}
        content={infoModal.content}
      />
    </Container>
  );
};

const ManualPostingPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Manual Posting Workbench">
      <ManualPostingPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default ManualPostingPage;
